import { useIntegrationsNavigation } from 'modules/Integrations/view/integrationsNavigation/useIntegrationsNavigation'
import {
  GreyGrey60,
  IconArrowRotateRightLeft,
  IconExclamationTriangle,
  IconPlus,
  IndigoIndigo60,
  Primary13Regular,
  Spinner,
  YellowYellow50,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { IntegrationsBrowserModal } from 'modules/Integrations/view/integrationsBrowserModal/IntegrationsBrowserModal'
import { IntegrationsNavLink } from 'modules/Integrations/view/integrationsNavigation/IntegrationsNavLink'
import { NavLinksLoading } from 'components/NavigationV3/components/NavLinks/NavLinksLoading'
import { NavSection } from 'components/NavigationV3/components/NavSection'
import { usePermissions } from 'lib/rbac'

interface IntegrationsNavigationProps {
  isCollapsed?: boolean
}

/**
 * Navigation component for Integrations section
 * Auto-expands when on integration management pages
 */
export const IntegrationsNavigation = ({
  isCollapsed = false
}: IntegrationsNavigationProps) => {
  const { can } = usePermissions()
  const { integrationsList, loading, error, reloadIntegrations } =
    useIntegrationsNavigation()

  // Only expand when on an integration management page
  const handlePathMatching = (pathname: string) =>
    pathname.includes('integration-management')

  return (
    <Box padding="8px 8px" userSelect="none">
      <NavSection
        id="integrations"
        title="Connectors"
        expandOnMatchingPath={handlePathMatching}
        isCollapsed={isCollapsed}
        data-testid="navgroup.integrations"
      >
        {loading &&
          (isCollapsed ? (
            <Flex
              justifyContent="center"
              alignItems="center"
              height="32px"
              padding="0 8px"
            >
              <Spinner height="16px" width="16px" color={GreyGrey60} />
            </Flex>
          ) : (
            <NavLinksLoading />
          ))}

        {error && (
          <Grid
            alignItems="center"
            height="32px"
            padding="0 8px"
            gridColumnGap="8px"
            gridTemplateColumns="auto 1fr auto"
            {...Primary13Regular}
            color={IndigoIndigo60}
            onClick={reloadIntegrations}
            cursor="pointer"
            data-testid="integrations.tryAgain"
          >
            <IconArrowRotateRightLeft />
            {!isCollapsed && (
              <span data-testid="integrations.tryAgainText">Try again</span>
            )}
            <IconExclamationTriangle
              style={{
                position: 'relative',
                top: '1px'
              }}
              color={YellowYellow50}
            />
          </Grid>
        )}

        {!loading && !error && (
          <>
            {integrationsList.integrations.map(integration => (
              <IntegrationsNavLink
                key={integration.id}
                active={integration.active}
                logo={integration.logo}
                label={integration.label}
                onClick={integration.onClick}
                status={integration.status}
                isCollapsed={isCollapsed}
              />
            ))}
            {can('org.integration:create') && (
              <IntegrationsBrowserModal
                trigger={
                  <IntegrationsNavLink
                    logo={() => <IconPlus />}
                    label="Add more"
                    isCollapsed={isCollapsed}
                    dataTestId="integrations.nav.add-more"
                  />
                }
              />
            )}
          </>
        )}
      </NavSection>
    </Box>
  )
}
